import { memo } from 'react';
import { Responsive, WidthProvider, Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { Box } from '@mui/material';
import { WidgetComponent } from '../widget-component';
import { WidgetType, WidgetTypeEnum } from '../../types/types';
import { TagResponse } from 'src/features/alarm-rules/types';
import { useResponsive } from 'src/hooks/useResponsive';
import { getWidgetsSortedByLayout } from '../../utils';
import { useFullscreen } from 'src/contexts/fullscreenContext';

type WidgetsListProps = {
  widgets: WidgetType[];
  setLayoutHandler: (layout: Layout[]) => void;
  layout: Layout[];
  isEditMode: boolean;
  isDesktop: boolean;
  toggleAddOrRemoveHandler: () => void;
  gatewayInputs: TagResponse[];
  isAddedController?: boolean;
  areGatewatInputsReady: boolean;
  position: {
    x: number;
    y: number;
  };
};

const GridLayout = WidthProvider(Responsive) as React.ComponentType<
  ReactGridLayout.ResponsiveProps & ReactGridLayout.WidthProviderProps
>;

const WidgetListInner = ({
  widgets,
  setLayoutHandler,
  layout,
  isEditMode,
  isDesktop,
  toggleAddOrRemoveHandler,
  gatewayInputs,
  isAddedController,
  areGatewatInputsReady,
  position,
}: WidgetsListProps) => {
  const isTablet = useResponsive('up', 'md');
  const { isFullscreen } = useFullscreen();

  const sortedWidgets = isDesktop ? widgets : getWidgetsSortedByLayout(widgets, layout);

  const listOfWidgets = sortedWidgets?.map((widget: WidgetType) => (
    <Box
      key={widget.id}
      sx={{
        ...(isTablet &&
          !isDesktop &&
          widget.widgetType === WidgetTypeEnum.LIFT_STATION_WIDGET && { gridColumn: 'span 2' }),
      }}
    >
      <WidgetComponent
        isEditMode={isEditMode}
        widget={widget}
        key={widget.id}
        toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
        gatewayInputs={gatewayInputs}
        isAddedController={isAddedController}
        areGatewatInputsReady={areGatewatInputsReady}
        isDesktop={isDesktop}
        position={position}
      />
    </Box>
  ));

  return (
    <>
      {isDesktop ? (
        <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
          <GridLayout
            onLayoutChange={(layout: Layout[]) => {
              setLayoutHandler(layout);
            }}
            className="layout"
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: 8, md: 6, sm: 4, xs: 2, xxs: 2 }}
            layouts={{ lg: layout }}
            isResizable={false}
            isDraggable={isEditMode}
          >
            {listOfWidgets}
          </GridLayout>
        </Box>
      ) : (
        <Box
          key={isFullscreen ? 'fullscreen' : 'normal'}
          sx={{
            display: 'grid',
            gridTemplateColumns: isTablet ? 'repeat(2,1fr)' : 'repeat(1,1fr)',
            gridGap: '20px',
          }}
        >
          {listOfWidgets}
        </Box>
      )}
    </>
  );
};

export const WidgetList = memo(WidgetListInner);
